import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Alert, Box, Link, Typography, useTheme } from '@mui/material';
import React from 'react';
import {
  IntakeChannelsCard,
  QuestionFlowSegmentedTabs,
  ReportTypeCard,
  SubmissionTypeCard,
} from '../molecules';
import {
  IntakeChannelsState,
  ReportType,
  SpeakUpReportedFieldsValue,
} from '../types';

interface Props {
  value: SpeakUpReportedFieldsValue;
  onChange: (value: SpeakUpReportedFieldsValue) => void;
  goTogetherEnabled: boolean;
  mode: 'create' | 'edit';
  onGoToFormCreation?: () => void;
  hideQuestionFlowTabs?: boolean;
}

export const SpeakUpReportedCategoryFields: React.FC<Props> = ({
  value,
  onChange,
  goTogetherEnabled,
  mode,
  onGoToFormCreation,
  hideQuestionFlowTabs,
}) => {
  const { tokens } = useTheme<LensTheme>();
  const isCustom = value.questionFlow === 'custom';
  const gap = tokens.semantic.spacing.desktop.gap[3].value;

  const patch = (partial: Partial<SpeakUpReportedFieldsValue>) =>
    onChange({ ...value, ...partial });

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap }}
      data-testid="su-reported-fields"
    >
      <QuestionFlowSegmentedTabs
        value={hideQuestionFlowTabs ? 'default' : value.questionFlow}
        onChange={(questionFlow) => {
          if (!hideQuestionFlowTabs) {
            patch({ questionFlow });
          }
        }}
        disabled={hideQuestionFlowTabs}
      />

      {hideQuestionFlowTabs ? (
        <>
          <ReportTypeCard
            value={value.reportType}
            onChange={(reportType: ReportType) => patch({ reportType })}
          />
          <IntakeChannelsCard
            value={value.channels}
            onChange={(channels: IntakeChannelsState) => patch({ channels })}
            forced={false}
            legacy
            manualIntake={value.manualIntake ?? false}
            onManualIntakeChange={(manualIntake) => patch({ manualIntake })}
          />
          <SubmissionTypeCard
            goTogether={value.goTogether}
            onGoTogetherChange={(goTogether) => patch({ goTogether })}
            goTogetherEnabled={goTogetherEnabled}
            legacy
          />
        </>
      ) : isCustom ? (
        <>
          {mode === 'create' && (
            <Alert severity="info" data-testid="form-required-banner">
              <Typography
                sx={{
                  fontWeight: tokens.semantic.fontWeight.emphasis.value,
                  fontSize: '14px',
                }}
              >
                Form required
              </Typography>
              <Typography sx={{ fontSize: '12px' }}>
                Categories with custom questions in Vault require a form before
                they can be set live. You can save your changes, but the
                category will only go live once a form is created.
              </Typography>
              <Link
                component="button"
                type="button"
                underline="always"
                sx={{ fontSize: '12px' }}
                onClick={onGoToFormCreation}
              >
                Go to Form creation →
              </Link>
            </Alert>
          )}
          <Alert severity="warning" data-testid="custom-flow-warning">
            Categories with custom questions can only be submitted via Vault.
          </Alert>
          <IntakeChannelsCard
            value={value.channels}
            onChange={(channels: IntakeChannelsState) => patch({ channels })}
            forced
          />
          {mode === 'edit' && (
            <SubmissionTypeCard
              goTogether={value.goTogether}
              onGoTogetherChange={(goTogether) => patch({ goTogether })}
              goTogetherEnabled={goTogetherEnabled}
            />
          )}
        </>
      ) : (
        <>
          <ReportTypeCard
            value={value.reportType}
            onChange={(reportType: ReportType) => patch({ reportType })}
          />
          <IntakeChannelsCard
            value={value.channels}
            onChange={(channels: IntakeChannelsState) => patch({ channels })}
            forced={false}
          />
          <SubmissionTypeCard
            goTogether={value.goTogether}
            onGoTogetherChange={(goTogether) => patch({ goTogether })}
            goTogetherEnabled={goTogetherEnabled}
          />
        </>
      )}
    </Box>
  );
};
